import { useEffect, useState } from 'react'
import { api, ApiError } from '../../services/api'
import { Modal, FormField, Alert, btnPrimary, btnSecondary, input, colors } from '../../components/ui'

interface ScheduledJobEditModalProps {
  readonly jobId: string | null
  readonly isOpen: boolean
  readonly isNew: boolean
  readonly onClose: () => void
  readonly onSaved: () => void
}

interface JobPayload {
  name: string
  kind: string
  cron: string
  enabled: boolean
}

// `kind` options come from the backend so the frontend doesn't
// duplicate the SCHEDULED_JOB_KINDS union (and surface a stale option
// after a handler kind is added/removed server-side).
export function ScheduledJobEditModal({ jobId, isOpen, isNew, onClose, onSaved }: ScheduledJobEditModalProps) {
  const [form, setForm] = useState<JobPayload>({
    name: '',
    kind: 'pdf_retention',
    cron: '0 3 * * *',
    enabled: true,
  })
  const [kinds, setKinds] = useState<readonly string[]>(['pdf_retention'])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    void api
      .get<{ kinds: string[] }>('/admin/scheduled-jobs/kinds')
      .then((res) => setKinds(res.data?.kinds ?? ['pdf_retention']))
      .catch(() => {
        // Fallback to the built-in default already in state.
      })
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || isNew || !jobId) return
    void api
      .get<JobPayload>(`/admin/scheduled-jobs`)
      .then((res) => {
        const rows = (res.data as unknown as { rows?: Array<JobPayload & { id: string }> }).rows ?? []
        const row = rows.find((r) => r.id === jobId)
        if (row) setForm({ name: row.name, kind: row.kind, cron: row.cron, enabled: row.enabled })
      })
      .catch(() => {})
  }, [isOpen, isNew, jobId])

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      if (isNew) {
        await api.post('/admin/scheduled-jobs', form)
      } else if (jobId) {
        await api.patch(`/admin/scheduled-jobs/${jobId}`, form)
      }
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isNew ? 'New Scheduled Job' : 'Edit Scheduled Job'} width="520px">
      {error && <Alert type="error" message={error} />}
      <div style={{ display: 'grid', gap: '0.75rem' }}>
        <FormField name="name" label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <FormField name="kind" label="Kind">
          <select
            id="kind"
            style={input}
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
          >
            {kinds.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </FormField>
        <FormField
          name="cron"
          label="Cron (5-field UTC)"
          value={form.cron}
          onChange={(v) => setForm({ ...form, cron: v })}
          placeholder="0 3 * * *"
        />
        <p style={{ fontSize: '0.75rem', color: colors.textMuted, margin: 0 }}>
          Examples: <code>0 3 * * *</code> (daily 03:00 UTC), <code>*/5 * * * *</code> (every 5 min),{' '}
          <code>0 0 1 * *</code> (first of month).
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            id="enabled"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />
          <label htmlFor="enabled" style={{ fontSize: '0.875rem', color: colors.text }}>
            Enabled
          </label>
        </div>
      </div>
      <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <button type="button" style={btnSecondary} onClick={onClose}>
          Cancel
        </button>
        <button type="button" style={btnPrimary} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </Modal>
  )
}
